import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { getExercise, startExercise, submitExercise, saveProgress } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Play, Clock, Target, Keyboard, Volume2, VolumeX, RotateCcw, Pause, ChevronRight, AlertTriangle, BookOpen } from 'lucide-react';

// Sound Effects Hook using Web Audio API
const useSoundEffects = () => {
  const audioContextRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Initialize audio context on first user interaction
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play a tone with given frequency and duration
  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.1) => {
    if (!soundEnabled) return;
    
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') ctx.resume();
      
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      
      // Fade in and out for smoother sound
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silently fail if audio doesn't work
    }
  }, [soundEnabled, initAudio]);

  // Keystroke sound - subtle click
  const playKeystroke = useCallback(() => {
    playTone(800, 0.05, 'sine', 0.05);
  }, [playTone]);

  // Correct word sound - pleasant ding
  const playCorrect = useCallback(() => {
    playTone(880, 0.1, 'sine', 0.08);
  }, [playTone]);

  // Error sound - soft buzz
  const playError = useCallback(() => {
    playTone(200, 0.15, 'square', 0.06);
  }, [playTone]);

  // Completion sound - success chime (ascending notes)
  const playComplete = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      const ctx = initAudio();
      if (ctx.state === 'suspended') ctx.resume();
      
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      
      notes.forEach((freq, i) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        
        const startTime = ctx.currentTime + (i * 0.1);
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.15, startTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(0, startTime + 0.3);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + 0.3);
      });
    } catch (e) {
      // Silently fail
    }
  }, [soundEnabled, initAudio]);

  // Timer warning sound - urgent beep
  const playWarning = useCallback(() => {
    playTone(440, 0.2, 'square', 0.1);
  }, [playTone]);

  return {
    soundEnabled,
    setSoundEnabled,
    playKeystroke,
    playCorrect,
    playError,
    playComplete,
    playWarning,
    initAudio
  };
};

// Minimum words required before allowing finish (prevents empty submissions)
const MINIMUM_WORDS_REQUIRED = 10;

const TypingExercise = () => {
  const navigate = useNavigate();
  const { exerciseId } = useParams();
  const textareaRef = useRef(null);
  const passageContainerRef = useRef(null);
  const timerRef = useRef(null);
  const autoSaveRef = useRef(null);
  const lastWordCountRef = useRef(0);
  const lastErrorCountRef = useRef(0);
  const pauseStartTimeRef = useRef(null);
  
  const [exercise, setExercise] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [typedText, setTypedText] = useState('');
  const [currentWPM, setCurrentWPM] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [finished, setFinished] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [resumed, setResumed] = useState(false);
  const [elapsedBeforeResume, setElapsedBeforeResume] = useState(0);
  const [paused, setPaused] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);  // Lock typing when timer expires
  const [showScoreFlash, setShowScoreFlash] = useState(false);  // Show score before quiz
  const [submissionResult, setSubmissionResult] = useState(null);  // Store submission result
  
  // Anti-cheat state
  const [warningCount, setWarningCount] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningReason, setWarningReason] = useState('');
  const [terminated, setTerminated] = useState(false);

  // Sound effects
  const { 
    soundEnabled, 
    setSoundEnabled, 
    playKeystroke, 
    playCorrect, 
    playError, 
    playComplete, 
    playWarning,
    initAudio 
  } = useSoundEffects();

  // Normalize text for comparison (convert curly quotes to straight quotes)
  // This ensures typed straight quotes match AI-generated curly quotes
  const normalizeText = useCallback((text) => {
    if (!text) return '';
    // Use explicit character codes to avoid any encoding issues
    const STRAIGHT_SINGLE = String.fromCharCode(0x0027); // '
    const STRAIGHT_DOUBLE = String.fromCharCode(0x0022); // "
    
    let normalized = text;
    
    // Replace ALL single quote variants with straight single quote
    // U+2018, U+2019, U+201A, U+201B, U+2032, U+0060, U+00B4, U+0027
    normalized = normalized.replace(/[\u2018\u2019\u201A\u201B\u2032\u0060\u00B4\u0027]/g, STRAIGHT_SINGLE);
    
    // Replace ALL double quote variants with straight double quote
    // U+201C, U+201D, U+201E, U+201F, U+2033, U+00AB, U+00BB, U+0022
    normalized = normalized.replace(/[\u201C\u201D\u201E\u201F\u2033\u00AB\u00BB\u0022]/g, STRAIGHT_DOUBLE);
    
    // Dashes: U+2013, U+2014, U+2015 -> U+002D
    normalized = normalized.replace(/[\u2013\u2014\u2015]/g, '-');
    
    // Ellipsis: U+2026 -> ...
    normalized = normalized.replace(/\u2026/g, '...');
    
    // Normalize whitespace
    normalized = normalized.replace(/\s+/g, ' ').trim();
    
    return normalized;
  }, []);

  // Normalize word for comparison - STRICT punctuation enforcement
  // Students must type exact punctuation (commas, periods, etc.) to get credit
  const normalizeWordForComparison = useCallback((word) => {
    if (!word) return '';
    let normalized = word;
    const STRAIGHT_SINGLE = String.fromCharCode(0x0027); // '
    const STRAIGHT_DOUBLE = String.fromCharCode(0x0022); // "
    
    // Normalize all quote variants to straight quotes (so curly quotes match straight)
    normalized = normalized.replace(/[\u2018\u2019\u201A\u201B\u2032\u0060\u00B4\u0027]/g, STRAIGHT_SINGLE);
    normalized = normalized.replace(/[\u201C\u201D\u201E\u201F\u2033\u00AB\u00BB\u0022]/g, STRAIGHT_DOUBLE);
    
    // Normalize dashes (em-dash, en-dash to regular hyphen)
    normalized = normalized.replace(/[\u2013\u2014\u2015]/g, '-');
    
    // Convert to lowercase for case-insensitive comparison
    normalized = normalized.toLowerCase();
    
    // STRICT MODE: Keep ALL punctuation - students must type it exactly
    // Only trim whitespace, not punctuation
    normalized = normalized.trim();
    
    return normalized;
  }, []);

  // Get all words from passage for comparison (normalized)
  const passageWords = useMemo(() => {
    if (!exercise?.passage) return [];
    return normalizeText(exercise.passage).split(/\s+/).filter(w => w.length > 0);
  }, [exercise?.passage, normalizeText]);

  // Get typed words for comparison (normalized)
  const typedWords = useMemo(() => {
    return normalizeText(typedText).split(/\s+/).filter(w => w.length > 0);
  }, [typedText, normalizeText]);

  // Calculate word-by-word comparison
  const wordComparison = useMemo(() => {
    return passageWords.map((word, index) => {
      if (index >= typedWords.length) {
        return { word, status: 'pending', typed: null };
      }
      const typedWord = typedWords[index];
      
      // Use lenient comparison that ignores case and surrounding punctuation
      const normalizedExpected = normalizeWordForComparison(word);
      const normalizedTyped = normalizeWordForComparison(typedWord);
      
      if (normalizedTyped === normalizedExpected) {
        return { word, status: 'correct', typed: typedWord };
      }
      return { word, status: 'incorrect', typed: typedWord };
    });
  }, [passageWords, typedWords, normalizeWordForComparison]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = passageWords.length;
    const typed = typedWords.length;
    const correct = wordComparison.filter(w => w.status === 'correct').length;
    const incorrect = wordComparison.filter(w => w.status === 'incorrect').length;
    
    const completion = total > 0 ? Math.round((typed / total) * 100) : 0;
    const accuracy = typed > 0 ? Math.round((correct / typed) * 100) : 0;
    
    return { total, typed, correct, incorrect, completion, accuracy };
  }, [passageWords, typedWords, wordComparison]);

  // Split passage into display lines
  const getPassageLines = useCallback(() => {
    if (!exercise?.passage) return [];
    const words = exercise.passage.split(' ');
    const lines = [];
    let currentLine = '';
    
    words.forEach(word => {
      if ((currentLine + ' ' + word).length > 60) {
        if (currentLine) lines.push(currentLine.trim());
        currentLine = word;
      } else {
        currentLine += ' ' + word;
      }
    });
    if (currentLine.trim()) lines.push(currentLine.trim());
    
    return lines;
  }, [exercise?.passage]);

  const passageLines = getPassageLines();

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const res = await getExercise(exerciseId);
        setExercise(res.data);
        setTimeLeft(res.data.duration_minutes * 60);
        
        // Check if there's an existing in-progress session with saved progress
        if (res.data.user_session?.status === 'in_progress') {
          setSession(res.data.user_session);
          const savedText = res.data.user_session.typed_text || '';
          const savedTime = res.data.user_session.time_spent_seconds || 0;
          
          if (savedText || savedTime > 0) {
            setTypedText(savedText);
            setElapsedBeforeResume(savedTime);
            setResumed(true);
            
            // Calculate remaining time
            const totalSeconds = res.data.duration_minutes * 60;
            const remaining = Math.max(0, totalSeconds - savedTime);
            setTimeLeft(remaining);
          }
        }
      } catch {
        toast.error('Failed to load exercise');
        navigate('/student');
      } finally {
        setLoading(false);
      }
    };
    fetchExercise();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveRef.current) clearInterval(autoSaveRef.current);
    };
  }, [exerciseId, navigate]);

  const handleStart = async () => {
    try {
      // Initialize audio on user interaction
      initAudio();
      
      const res = await startExercise(exerciseId);
      setSession(res.data);
      
      // If this is a reading-only exercise, skip typing and go straight to quiz with passage
      if (exercise?.exercise_type === 'reading') {
        const sessionId = res.data.session_id;
        // Navigate to quiz page where they'll see the passage and answer questions
        navigate(`/quiz/${exerciseId}/${sessionId}?reading=true`);
        return;
      }
      
      setStarted(true);
      
      // Check if resuming with saved progress
      if (res.data.resumed && res.data.saved_progress) {
        const savedProgress = res.data.saved_progress;
        setTypedText(savedProgress.typed_text || '');
        setElapsedBeforeResume(savedProgress.time_spent_seconds || 0);
        setResumed(true);
        
        // Calculate remaining time
        const totalSeconds = exercise.duration_minutes * 60;
        const remaining = Math.max(0, totalSeconds - savedProgress.time_spent_seconds);
        setTimeLeft(remaining);
        
        toast.info('Resuming from where you left off!');
      }
      
      setStartTime(Date.now());
      textareaRef.current?.focus();
      
      // Start auto-save every 10 seconds
      autoSaveRef.current = setInterval(() => {
        handleAutoSave();
      }, 10000);
      
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          // Play warning at 30, 10, 5, 4, 3, 2, 1 seconds
          if ([30, 10, 5, 4, 3, 2, 1].includes(prev)) {
            playWarning();
          }
          
          if (prev <= 1) {
            clearInterval(timerRef.current);
            clearInterval(autoSaveRef.current);
            // Don't auto-finish - set timerExpired to lock typing and prompt finish
            setTimerExpired(true);
            playComplete();  // Play sound to alert student
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error('Failed to start exercise');
    }
  };

  // Auto-save progress
  const handleAutoSave = useCallback(async () => {
    if (!started || finished || paused) return;
    
    const currentElapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const totalElapsed = elapsedBeforeResume + currentElapsed;
    
    try {
      await saveProgress(exerciseId, typedText, totalElapsed);
    } catch (error) {
      // Silently fail - don't interrupt the user
      console.error('Auto-save failed:', error);
    }
  }, [exerciseId, typedText, started, finished, paused, startTime, elapsedBeforeResume]);

  // Pause exercise
  const handlePause = useCallback(async () => {
    if (paused || finished) return;
    
    setPaused(true);
    pauseStartTimeRef.current = Date.now();
    
    // Stop timers
    clearInterval(timerRef.current);
    clearInterval(autoSaveRef.current);
    
    // Save progress immediately
    const currentElapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const totalElapsed = elapsedBeforeResume + currentElapsed;
    
    try {
      await saveProgress(exerciseId, typedText, totalElapsed);
      toast.info('Exercise paused. Your progress has been saved.');
    } catch (error) {
      console.error('Save on pause failed:', error);
    }
  }, [exerciseId, typedText, paused, finished, startTime, elapsedBeforeResume]);

  // Resume exercise
  const handleResume = useCallback(() => {
    if (!paused) return;
    
    setPaused(false);
    
    // Adjust start time to account for pause duration
    // This ensures the elapsed time calculation remains accurate
    if (pauseStartTimeRef.current && startTime) {
      const pauseDuration = Date.now() - pauseStartTimeRef.current;
      setStartTime(prev => prev + pauseDuration);
    }
    pauseStartTimeRef.current = null;
    
    // Restart timers
    autoSaveRef.current = setInterval(() => {
      handleAutoSave();
    }, 10000);
    
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if ([30, 10, 5, 4, 3, 2, 1].includes(prev)) {
          playWarning();
        }
        
        if (prev <= 1) {
          clearInterval(timerRef.current);
          clearInterval(autoSaveRef.current);
          // Note: handleFinish will be called via the timeLeft effect
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    toast.success('Exercise resumed!');
    textareaRef.current?.focus();
  }, [paused, startTime, handleAutoSave, playWarning]);

  // Handle timer reaching zero
  // Note: We no longer auto-call handleFinish when timer expires
  // Instead, we set timerExpired=true and require the student to click Finish
  // This gives them a chance to review their work before submitting

  // Save progress when user leaves page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (started && !finished) {
        const currentElapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
        const totalElapsed = elapsedBeforeResume + currentElapsed;
        // Use sendBeacon for reliable save on page unload
        const data = JSON.stringify({
          typed_text: typedText,
          time_spent_seconds: totalElapsed
        });
        navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/exercises/${exerciseId}/save-progress`, 
          new Blob([data], { type: 'application/json' }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [exerciseId, typedText, started, finished, startTime, elapsedBeforeResume]);

  const handleFinish = useCallback(async () => {
    if (finished || submitting) return;
    setSubmitting(true);
    setSubmitError(false);
    setFinished(true);
    clearInterval(timerRef.current);
    clearInterval(autoSaveRef.current);
    
    // Play completion sound
    playComplete();
    
    const currentElapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const totalTimeSpent = elapsedBeforeResume + currentElapsed;
    
    // IMPORTANT: Capture the current typed text immediately
    // This ensures we submit what was actually typed, even on timeout
    const textToSubmit = typedText || '';
    
    // Log for debugging timeout submissions
    const wordsTyped = textToSubmit.trim().split(/\s+/).filter(w => w.length > 0).length;
    console.log(`Submitting exercise: ${wordsTyped} words typed, ${totalTimeSpent}s elapsed`);
    
    try {
      const res = await submitExercise(exerciseId, {
        exercise_id: exerciseId,
        typed_text: textToSubmit,
        time_spent_seconds: totalTimeSpent
      });
      
      // Use session_id from response (more reliable than state)
      const sessionId = res.data.session_id || session?.session_id;
      
      if (!sessionId) {
        console.error('No session_id available after submit');
        toast.error('Session error - please try again');
        setSubmitting(false);
        setSubmitError(true);
        setFinished(false);
        return;
      }
      
      // Store result and show score flash screen
      setSubmissionResult({
        ...res.data,
        sessionId
      });
      setShowScoreFlash(true);
      setSubmitting(false);
      
    } catch (error) {
      console.error('Submit exercise error:', error);
      toast.error('Failed to submit. Your work is saved - click "Try Again"');
      // Reset states so they can try again
      setSubmitting(false);
      setSubmitError(true);
      setFinished(false);
    }
  }, [exerciseId, typedText, startTime, session, finished, submitting, playComplete, elapsedBeforeResume]);

  // Navigate to quiz after score flash
  const proceedToQuiz = useCallback(() => {
    if (submissionResult?.sessionId) {
      navigate(`/quiz/${exerciseId}/${submissionResult.sessionId}`);
    }
  }, [exerciseId, submissionResult, navigate]);

  // Calculate which line based on character position
  const calculateCurrentLine = useCallback((text) => {
    if (!text || !passageLines.length) return 0;
    
    let charCount = 0;
    for (let i = 0; i < passageLines.length; i++) {
      charCount += passageLines[i].length + 1; // +1 for space between lines
      if (text.length < charCount) {
        return i;
      }
    }
    return passageLines.length - 1;
  }, [passageLines]);

  const handleTextChange = (e) => {
    // Block typing if timer has expired
    if (timerExpired) {
      return;
    }
    
    const newText = e.target.value;
    const prevWords = normalizeText(typedText).split(/\s+/).filter(w => w.length > 0);
    const newWords = normalizeText(newText).split(/\s+/).filter(w => w.length > 0);
    
    // Play keystroke sound on typing
    if (newText.length > typedText.length) {
      playKeystroke();
    }
    
    // Check if a new word was completed (space pressed after typing)
    if (newWords.length > prevWords.length && newText.endsWith(' ')) {
      const lastWordIndex = newWords.length - 1;
      const expectedWord = passageWords[lastWordIndex];
      const typedWord = newWords[lastWordIndex];
      
      // Use lenient comparison
      const normalizedExpected = normalizeWordForComparison(expectedWord);
      const normalizedTyped = normalizeWordForComparison(typedWord);
      
      if (expectedWord && normalizedTyped === normalizedExpected) {
        playCorrect();
      } else if (expectedWord) {
        playError();
      }
    }
    
    setTypedText(newText);
    
    // DON'T auto-finish based on word count - it's unreliable with punctuation
    // Students should click Finish or let the timer run out
    // This prevents cutting them off mid-passage
    
    // Calculate WPM using standard formula: (characters / 5) / minutes
    // Standard typing tests count 5 characters as 1 "word"
    if (startTime) {
      const minutes = (Date.now() - startTime) / 60000;
      const characters = newText.length;
      const standardWords = characters / 5;  // Standard: 5 chars = 1 word
      const currentWpm = Math.round(standardWords / (minutes || 0.01));
      setCurrentWPM(currentWpm);
    }
    
    // ANTI-CHEAT: Check for nonsense/gibberish words
    // A word is considered gibberish if it has 5+ letters and:
    // - Has no vowels, OR
    // - Has 4+ consecutive consonants, OR
    // - Is the same letter repeated (like "aaaaa"), OR
    // - Is a keyboard mash pattern (like "asdfg", "qwerty")
    const isGibberish = (word) => {
      if (!word || word.length < 5) return false;
      
      const lowerWord = word.toLowerCase();
      const vowels = 'aeiou';
      const consonants = 'bcdfghjklmnpqrstvwxyz';
      
      // Check for no vowels
      const hasVowel = [...lowerWord].some(c => vowels.includes(c));
      if (!hasVowel) return true;
      
      // Check for 4+ consecutive consonants
      let consecutiveConsonants = 0;
      for (const char of lowerWord) {
        if (consonants.includes(char)) {
          consecutiveConsonants++;
          if (consecutiveConsonants >= 4) return true;
        } else {
          consecutiveConsonants = 0;
        }
      }
      
      // Check for same letter repeated 4+ times
      for (let i = 0; i < lowerWord.length - 3; i++) {
        if (lowerWord[i] === lowerWord[i+1] && 
            lowerWord[i] === lowerWord[i+2] && 
            lowerWord[i] === lowerWord[i+3]) {
          return true;
        }
      }
      
      // Check for keyboard mash patterns
      const keyboardPatterns = ['asdf', 'qwer', 'zxcv', 'hjkl', 'yuio', 'bnm,', 'fghj', 'ghjk', 'dfgh', 'cvbn', 'xcvb', 'sdfg', 'wert', 'erty', 'rtyu', 'tyui', 'uiop'];
      for (const pattern of keyboardPatterns) {
        if (lowerWord.includes(pattern)) return true;
      }
      
      return false;
    };
    
    // Check the most recently completed word for gibberish
    // (only when a space is pressed, meaning a word was just completed)
    if (newWords.length > prevWords.length && newText.endsWith(' ') && !terminated && !showWarningModal) {
      const lastTypedWord = newWords[newWords.length - 1];
      const expectedWordIndex = newWords.length - 1;
      const expectedWord = passageWords[expectedWordIndex];
      
      // Only flag if the word doesn't match expected AND is gibberish
      if (expectedWord) {
        const normalizedTyped = normalizeWordForComparison(lastTypedWord);
        const normalizedExpected = normalizeWordForComparison(expectedWord);
        
        if (normalizedTyped !== normalizedExpected && isGibberish(lastTypedWord)) {
          console.log(`ANTI-CHEAT: Gibberish word detected: "${lastTypedWord}" (expected: "${expectedWord}")`);
          triggerWarning('gibberish', `"${lastTypedWord}" is not a valid word. Please type the passage correctly.`);
        }
      }
    }
    
    // Calculate current line and scroll passage
    const newLineIndex = calculateCurrentLine(newText);
    setCurrentLineIndex(newLineIndex);
    
    // IMPROVED: Much gentler scrolling - only scroll when absolutely needed
    // Only scroll when user moves to a new line AND that line is out of view
    if (passageContainerRef.current && newLineIndex !== currentLineIndex) {
      const lineHeight = 72; // Approximate height of each line block
      const containerHeight = passageContainerRef.current.clientHeight;
      const currentScroll = passageContainerRef.current.scrollTop;
      const currentLineTop = newLineIndex * lineHeight;
      const currentLineBottom = currentLineTop + lineHeight;
      
      // Calculate visible area
      const visibleTop = currentScroll;
      const visibleBottom = currentScroll + containerHeight;
      
      // Only scroll if the current line is mostly out of the visible area
      // Give a buffer of 2 lines at top and bottom
      const topBuffer = lineHeight * 2;
      const bottomBuffer = lineHeight * 2;
      
      if (currentLineBottom > visibleBottom - bottomBuffer) {
        // Line is too close to bottom - scroll down gently
        // Keep the current line in the middle of the view
        const targetScroll = Math.max(0, currentLineTop - (containerHeight / 2));
        passageContainerRef.current.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      } else if (currentLineTop < visibleTop + topBuffer && visibleTop > 0) {
        // Line is too close to top and we're not at the start - scroll up
        const targetScroll = Math.max(0, currentLineTop - topBuffer);
        passageContainerRef.current.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
    }
  };
  
  // Anti-cheat warning system
  const triggerWarning = useCallback((type, message) => {
    playWarning();
    setWarningReason(message);
    setShowWarningModal(true);
    
    const newCount = warningCount + 1;
    setWarningCount(newCount);
    
    console.log(`ANTI-CHEAT WARNING #${newCount}: ${type} - ${message}`);
    
    if (newCount >= 2) {
      // Terminate exercise after 2 warnings
      setTerminated(true);
    }
  }, [warningCount, playWarning]);
  
  // Handle termination - submit with 0 scores
  const handleTermination = useCallback(async () => {
    setFinished(true);
    clearInterval(timerRef.current);
    clearInterval(autoSaveRef.current);
    
    const currentElapsed = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;
    const totalTimeSpent = elapsedBeforeResume + currentElapsed;
    
    try {
      const res = await submitExercise(exerciseId, {
        exercise_id: exerciseId,
        typed_text: '', // Submit empty to get 0%
        time_spent_seconds: totalTimeSpent,
        termination_reason: 'cheat_detected'
      });
      
      const sessionId = res.data.session_id || session?.session_id;
      
      // Navigate to quiz but they'll have 0% typing score
      if (sessionId) {
        toast.error('Exercise terminated due to policy violations. Typing score: 0%');
        navigate(`/quiz/${exerciseId}/${sessionId}`);
      }
    } catch (error) {
      console.error('Termination submit error:', error);
      navigate('/student');
    }
  }, [exerciseId, startTime, session, navigate, elapsedBeforeResume]);
  
  // Handle warning acknowledgment
  const acknowledgeWarning = useCallback(() => {
    setShowWarningModal(false);
    
    if (terminated) {
      // Auto-submit with zeros
      handleTermination();
    }
  }, [terminated, handleTermination]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!started) {
    // Check if this is a reading-only exercise
    const isReadingOnly = exercise?.exercise_type === 'reading';
    
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/student')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">{exercise?.title}</h1>
              <p className="text-text-secondary text-sm">
                {exercise?.objectives?.length > 1 
                  ? exercise.objectives.map(o => o.code).join(', ')
                  : exercise?.objective_code}
              </p>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <Card className="card-glass p-12 text-center">
            <div className="w-20 h-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6 neon-border">
              {isReadingOnly ? (
                <BookOpen className="w-10 h-10 text-secondary" />
              ) : resumed ? (
                <RotateCcw className="w-10 h-10 text-neon-yellow" />
              ) : (
                <Keyboard className="w-10 h-10 text-primary" />
              )}
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">
              {isReadingOnly ? 'Reading Quiz' : resumed ? 'Resume Your Progress' : 'Ready to Start?'}
            </h2>
            
            {isReadingOnly && (
              <div className="bg-secondary/10 border border-secondary/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <p className="text-secondary font-medium mb-2">Reading Comprehension</p>
                <p className="text-text-secondary text-sm">
                  Read the passage carefully, then answer <span className="text-secondary font-bold">{exercise?.quiz_questions?.length || 5} questions</span> to test your understanding.
                </p>
              </div>
            )}
            
            {!isReadingOnly && resumed && (
              <div className="bg-neon-yellow/10 border border-neon-yellow/30 rounded-lg p-4 mb-6 max-w-md mx-auto">
                <p className="text-neon-yellow font-medium mb-2">Saved Progress Found!</p>
                <p className="text-text-secondary text-sm">
                  You&apos;ve typed <span className="text-primary font-bold">{typedText.split(/\s+/).filter(w => w).length} words</span> and 
                  used <span className="text-primary font-bold">{Math.floor(elapsedBeforeResume / 60)}:{(elapsedBeforeResume % 60).toString().padStart(2, '0')}</span> of your time.
                </p>
              </div>
            )}
            
            <p className="text-text-secondary mb-8 max-w-md mx-auto">
              {isReadingOnly ? (
                <>Take your time to read and understand the passage before starting the quiz.</>
              ) : resumed ? (
                <>You have <span className="text-primary font-bold">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span> remaining.</>
              ) : (
                <>You will have <span className="text-primary font-bold">{exercise?.duration_minutes} minutes</span> to 
                type the passage. Target speed: <span className="text-primary font-bold">{exercise?.target_wpm} WPM</span></>
              )}
            </p>
            
            {!isReadingOnly && (
              <div className="flex justify-center gap-8 mb-8">
                <div className="text-center">
                  <Clock className="w-6 h-6 text-text-muted mx-auto mb-2" />
                  <p className="text-text-secondary text-sm">{resumed ? 'Time Left' : 'Duration'}</p>
                  <p className="font-heading text-xl font-bold text-primary">
                    {resumed ? `${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}` : `${exercise?.duration_minutes} min`}
                  </p>
                </div>
                <div className="text-center">
                  <Target className="w-6 h-6 text-text-muted mx-auto mb-2" />
                  <p className="text-text-secondary text-sm">Target WPM</p>
                  <p className="font-heading text-xl font-bold text-primary">{exercise?.target_wpm}</p>
                </div>
              </div>
            )}
            
            {isReadingOnly && (
              <div className="flex justify-center gap-8 mb-8">
                <div className="text-center">
                  <BookOpen className="w-6 h-6 text-text-muted mx-auto mb-2" />
                  <p className="text-text-secondary text-sm">Questions</p>
                  <p className="font-heading text-xl font-bold text-secondary">{exercise?.quiz_questions?.length || 5}</p>
                </div>
              </div>
            )}
            
            {/* Sound indicator - only for typing exercises */}
            {!isReadingOnly && (
              <div className="flex items-center gap-2 mb-6 text-text-muted text-sm justify-center">
                <Volume2 className="w-4 h-4 text-neon-green" />
                <span>Sound effects enabled - hear feedback as you type!</span>
              </div>
            )}
            
            <Button 
              className={isReadingOnly ? "btn-secondary px-12 py-4 text-lg rounded-sm" : "btn-primary px-12 py-4 text-lg rounded-sm"}
              onClick={handleStart}
              data-testid="start-exercise-btn"
            >
              {isReadingOnly ? (
                <>
                  <BookOpen className="w-5 h-5 mr-2" />
                  Start Reading
                </>
              ) : resumed ? (
                <>
                  <RotateCcw className="w-5 h-5 mr-2" />
                  Resume Typing
                </>
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Start Typing
                </>
              )}
            </Button>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Floating HUD */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 bg-background-paper/95 backdrop-blur-xl border border-white/10 rounded-full px-5 py-2 shadow-glass">
        <div className="text-center">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Time</p>
          <p className={`font-mono text-xl font-bold ${timeLeft < 30 ? 'text-neon-red animate-pulse' : 'text-primary'}`}>
            {formatTime(timeLeft)}
          </p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div className="text-center">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Words</p>
          <p className={`font-mono text-xl font-bold ${typedWords.length >= MINIMUM_WORDS_REQUIRED ? 'text-neon-green' : 'text-neon-yellow'}`}>
            {typedWords.length}
            {typedWords.length < MINIMUM_WORDS_REQUIRED && (
              <span className="text-xs text-text-muted">/{MINIMUM_WORDS_REQUIRED}</span>
            )}
          </p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div className="text-center">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">WPM</p>
          <p className="font-mono text-xl font-bold text-secondary">{currentWPM}</p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div className="text-center">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Accuracy</p>
          <p className={`font-mono text-xl font-bold ${stats.accuracy >= 80 ? 'text-neon-green' : stats.accuracy >= 60 ? 'text-neon-yellow' : 'text-neon-red'}`}>
            {stats.accuracy}%
          </p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div className="text-center">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Progress</p>
          <p className="font-mono text-xl font-bold text-neon-yellow">{stats.completion}%</p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        <div className="text-center">
          <p className="text-[10px] text-text-muted uppercase tracking-wider">Errors</p>
          <p className={`font-mono text-xl font-bold ${stats.incorrect > 0 ? 'text-neon-red' : 'text-neon-green'}`}>
            {stats.incorrect}
          </p>
        </div>
        <div className="w-px h-8 bg-white/10" />
        {/* Sound Toggle */}
        <button
          onClick={() => setSoundEnabled(!soundEnabled)}
          className={`p-2 rounded-full transition-colors ${soundEnabled ? 'text-neon-green hover:bg-neon-green/20' : 'text-text-muted hover:bg-white/10'}`}
          title={soundEnabled ? 'Sound On' : 'Sound Off'}
          data-testid="sound-toggle"
        >
          {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
        </button>
        <Button 
          size="sm"
          variant="outline"
          className="rounded-full ml-2 px-4 border-neon-yellow/50 text-neon-yellow hover:bg-neon-yellow/20"
          onClick={handlePause}
          disabled={submitting}
          data-testid="pause-btn"
        >
          <Pause className="w-4 h-4 mr-1" />
          Pause
        </Button>
        <div className="relative group">
          <Button 
            size="sm"
            className={`btn-primary rounded-full px-4 ${typedWords.length < MINIMUM_WORDS_REQUIRED && !timerExpired ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleFinish}
            disabled={submitting || (typedWords.length < MINIMUM_WORDS_REQUIRED && !timerExpired)}
            data-testid="finish-early-btn"
          >
            {submitting ? 'Submitting...' : 'Finish'}
          </Button>
          {typedWords.length < MINIMUM_WORDS_REQUIRED && !timerExpired && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-black/90 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Type at least {MINIMUM_WORDS_REQUIRED} words first ({typedWords.length}/{MINIMUM_WORDS_REQUIRED})
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black/90" />
            </div>
          )}
        </div>
      </div>

      {/* Submitting Overlay */}
      {submitting && (
        <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center">
          <Card className="card-glass p-12 text-center max-w-md mx-4">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-2xl font-bold mb-4">Submitting...</h2>
            <p className="text-text-secondary">
              Please wait while we save your work.
            </p>
          </Card>
        </div>
      )}

      {/* Submit Error Overlay */}
      {submitError && !submitting && (
        <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center">
          <Card className="card-glass p-12 text-center max-w-md mx-4">
            <div className="w-20 h-20 rounded-2xl bg-neon-red/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⚠️</span>
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">Submit Failed</h2>
            <p className="text-text-secondary mb-2">
              Don&apos;t worry - your work is saved!
            </p>
            <p className="text-text-muted text-sm mb-6">
              You typed <span className="text-primary font-bold">{typedWords.length} words</span>. 
              Click below to try submitting again.
            </p>
            <div className="flex gap-3 justify-center">
              <Button 
                variant="outline"
                onClick={() => setSubmitError(false)}
                className="px-6"
              >
                Continue Typing
              </Button>
              <Button 
                className="btn-primary px-6"
                onClick={handleFinish}
              >
                Try Again
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Pause Overlay */}
      {paused && (
        <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center">
          <Card className="card-glass p-12 text-center max-w-md mx-4">
            <div className="w-20 h-20 rounded-2xl bg-neon-yellow/20 flex items-center justify-center mx-auto mb-6">
              <Pause className="w-10 h-10 text-neon-yellow" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4">Exercise Paused</h2>
            <p className="text-text-secondary mb-6">
              Your progress has been saved. Take your time - the timer is stopped.
            </p>
            
            <div className="bg-black/30 rounded-lg p-4 mb-6 border border-white/10">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase">Time Remaining</p>
                  <p className="font-mono text-2xl font-bold text-primary">
                    {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase">Words Typed</p>
                  <p className="font-mono text-2xl font-bold text-neon-green">
                    {typedWords.length}
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              className="btn-primary px-12 py-4 text-lg rounded-sm w-full"
              onClick={handleResume}
              data-testid="resume-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Resume Typing
            </Button>
          </Card>
        </div>
      )}

      {/* Timer Expired Overlay - Require student to click Finish */}
      {timerExpired && !finished && !showScoreFlash && (
        <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center">
          <Card className="card-glass p-12 text-center max-w-md mx-4 animate-pulse">
            <div className="w-20 h-20 rounded-2xl bg-neon-red/20 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-neon-red" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-4 text-neon-red">Time's Up!</h2>
            <p className="text-text-secondary mb-6">
              You can no longer type. Review your work and click Finish to submit.
            </p>
            
            <div className="bg-black/30 rounded-lg p-4 mb-6 border border-white/10">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase">Words Typed</p>
                  <p className="font-mono text-2xl font-bold text-primary">
                    {typedWords.length}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase">Current Accuracy</p>
                  <p className={`font-mono text-2xl font-bold ${
                    stats.accuracy >= 80 ? 'text-neon-green' : 
                    stats.accuracy >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                  }`}>
                    {stats.accuracy}%
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              className="btn-primary px-12 py-4 text-lg rounded-sm w-full"
              onClick={handleFinish}
              disabled={submitting}
              data-testid="finish-after-timeout-btn"
            >
              {submitting ? 'Submitting...' : 'Finish & Submit'}
            </Button>
          </Card>
        </div>
      )}

      {/* Score Flash Overlay - Show score before navigating to quiz */}
      {showScoreFlash && submissionResult && (
        <div className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center">
          <Card className="card-glass p-12 text-center max-w-lg mx-4">
            <div className="w-20 h-20 rounded-2xl bg-neon-green/20 flex items-center justify-center mx-auto mb-6">
              <Target className="w-10 h-10 text-neon-green" />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Typing Complete!</h2>
            <p className="text-text-secondary mb-6">Here's how you did:</p>
            
            <div className="bg-black/30 rounded-lg p-6 mb-6 border border-white/10">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase mb-2">Accuracy</p>
                  <p className={`font-mono text-3xl font-bold ${
                    submissionResult.accuracy >= 80 ? 'text-neon-green' : 
                    submissionResult.accuracy >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                  }`}>
                    {submissionResult.accuracy}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase mb-2">Completion</p>
                  <p className={`font-mono text-3xl font-bold ${
                    submissionResult.completion >= 80 ? 'text-neon-green' : 
                    submissionResult.completion >= 50 ? 'text-neon-yellow' : 'text-neon-red'
                  }`}>
                    {submissionResult.completion}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-text-muted text-xs uppercase mb-2">Speed</p>
                  <p className="font-mono text-3xl font-bold text-secondary">
                    {submissionResult.actual_wpm}
                  </p>
                  <p className="text-text-muted text-xs">WPM</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-text-muted text-sm">
                  {submissionResult.correct_words} / {submissionResult.total_words} words correct
                </p>
              </div>
              
              {submissionResult.wpm_flagged && (
                <div className="mt-4 p-3 bg-neon-red/20 border border-neon-red/30 rounded-lg">
                  <p className="text-neon-red text-sm">
                    ⚠️ Your typing speed seems unusually high. Your teacher may review this submission.
                  </p>
                </div>
              )}
            </div>
            
            <Button 
              className="btn-primary px-12 py-4 text-lg rounded-sm w-full"
              onClick={proceedToQuiz}
              data-testid="proceed-to-quiz-btn"
            >
              Continue to Quiz
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Card>
        </div>
      )}

      {/* Anti-Cheat Warning Modal */}
      {showWarningModal && (
        <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-xl flex items-center justify-center">
          <Card className={`card-glass p-8 text-center max-w-md mx-4 border-2 ${terminated ? 'border-neon-red' : 'border-neon-orange'}`}>
            <div className={`w-16 h-16 rounded-2xl ${terminated ? 'bg-neon-red/20' : 'bg-neon-orange/20'} flex items-center justify-center mx-auto mb-4`}>
              <AlertTriangle className={`w-8 h-8 ${terminated ? 'text-neon-red' : 'text-neon-orange'}`} />
            </div>
            
            {terminated ? (
              <>
                <h2 className="font-heading text-2xl font-bold mb-2 text-neon-red">Exercise Terminated</h2>
                <p className="text-text-secondary mb-4">
                  Due to multiple policy violations, this exercise has been stopped.
                </p>
                <div className="bg-neon-red/10 border border-neon-red/30 rounded-lg p-4 mb-6">
                  <p className="text-neon-red font-bold text-lg mb-1">Typing Score: 0%</p>
                  <p className="text-text-muted text-sm">Your teacher has been notified.</p>
                </div>
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-bold mb-2 text-neon-orange">
                  Warning {warningCount}/2
                </h2>
                <p className="text-text-secondary mb-4">{warningReason}</p>
                <div className="bg-neon-orange/10 border border-neon-orange/30 rounded-lg p-4 mb-6">
                  <p className="text-neon-orange text-sm">
                    ⚠️ One more violation will terminate this exercise with a 0% typing score.
                  </p>
                </div>
              </>
            )}
            
            <Button 
              className={`px-8 py-3 text-lg rounded-sm w-full ${terminated ? 'bg-neon-red hover:bg-neon-red/80' : 'bg-neon-orange hover:bg-neon-orange/80'} text-black font-bold`}
              onClick={acknowledgeWarning}
              data-testid="acknowledge-warning-btn"
            >
              {terminated ? 'Continue to Quiz' : 'I Understand'}
            </Button>
          </Card>
        </div>
      )}

      {/* Split Screen */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 pt-16 h-screen">
        {/* Left: Passage with word-by-word feedback */}
        <div className="border-r border-white/5 bg-background-paper/30 flex flex-col h-full">
          <div className="px-6 py-3 border-b border-white/5 flex justify-between items-center">
            <h3 className="font-heading text-sm text-text-muted uppercase tracking-wider">
              Source Passage • {stats.typed} / {stats.total} words
            </h3>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-neon-green/30 border border-neon-green"></span>
                Correct
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-neon-red/30 border border-neon-red"></span>
                Error
              </span>
            </div>
          </div>
          <div 
            ref={passageContainerRef}
            className="flex-1 overflow-y-auto px-6 py-4"
            style={{ maxHeight: 'calc(100vh - 120px)' }}
          >
            <div className="max-w-lg mx-auto">
              <p className="font-mono leading-loose text-lg">
                {wordComparison.map((item, idx) => {
                  let className = 'inline-block px-0.5 py-0.5 mx-0.5 rounded transition-all ';
                  
                  if (item.status === 'correct') {
                    className += 'bg-neon-green/20 text-neon-green';
                  } else if (item.status === 'incorrect') {
                    className += 'bg-neon-red/20 text-white underline decoration-neon-red decoration-wavy decoration-2 underline-offset-4';
                  } else if (idx === typedWords.length) {
                    // Current word being typed
                    className += 'bg-primary/30 text-primary border border-primary/50';
                  } else {
                    className += 'text-text-secondary';
                  }
                  
                  return (
                    <span key={idx} className={className}>
                      {item.word}
                      {item.status === 'incorrect' && (
                        <span className="text-[10px] text-neon-red ml-1 font-bold">
                          ({item.typed})
                        </span>
                      )}
                    </span>
                  );
                })}
              </p>
              {/* Extra padding at bottom for scroll */}
              <div className="h-32" />
            </div>
          </div>
        </div>

        {/* Right: Typing Area - Fixed height, no overflow */}
        <div className="bg-background flex flex-col h-full">
          <div className="px-6 py-3 border-b border-white/5">
            <h3 className="font-heading text-sm text-text-muted uppercase tracking-wider">
              Type Here • {typedWords.length} words
            </h3>
          </div>
          <div className="flex-1 p-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            <div className="max-w-lg mx-auto w-full flex-1 flex flex-col">
              <textarea
                ref={textareaRef}
                className={`flex-1 w-full bg-black/40 border border-white/10 rounded-lg p-4 font-mono text-lg leading-relaxed resize-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none overflow-y-auto ${(timerExpired || terminated) ? 'opacity-50 cursor-not-allowed' : ''}`}
                value={typedText}
                onChange={handleTextChange}
                onPaste={(e) => {
                  e.preventDefault();
                  toast.error('Pasting is not allowed - please type the passage yourself!');
                  triggerWarning('paste', 'Pasting is not allowed. You must type the passage yourself.');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  toast.error('Drag and drop is not allowed - please type the passage yourself!');
                }}
                disabled={timerExpired || terminated}
                placeholder="Start typing the passage from the left..."
                autoFocus
                spellCheck={false}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                data-testid="typing-textarea"
              />
              <p className="text-text-muted text-sm mt-3 text-center">
                {terminated
                  ? "Exercise terminated. Click the warning to continue."
                  : timerExpired 
                    ? "Time's up! Click 'Finish & Submit' above to continue"
                    : "Keep your eyes on the passage to the left and type what you see"
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TypingExercise;
